import React from "react"
import {
  type EntityLegalEntity,
  useReadUserAclQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import useSession from "@digits-shared/hooks/useSession"
import type FrontendSession from "src/frontend/session"

export function useAccessibleLegalEntitiesById(includeSharedEntities: boolean) {
  const { jwtOrganizations } = useSession<FrontendSession>()
  const { data: aclData } = useReadUserAclQuery({
    fetchPolicy: "network-only",
    skip: !includeSharedEntities,
  })

  const aclBasedLes: EntityLegalEntity[] = React.useMemo(
    () => aclData?.readUserACL.entities.legalEntities?.slice() ?? [],
    [aclData]
  )

  const orgBasedLes: EntityLegalEntity[] = React.useMemo(
    () =>
      jwtOrganizations.flatMap((o) => {
        const dashboardLEs = o.dashboardLegalEntities.slice()
        const affiliateLEs = o.affiliatedLegalEntities.slice()
        return dashboardLEs.concat(affiliateLEs)
      }),
    [jwtOrganizations]
  )

  return React.useMemo(
    () =>
      // Org based LEs come second since they should take precedence over ACL-based LEs.
      (includeSharedEntities ? aclBasedLes : []).concat(orgBasedLes).reduce((agg, le) => {
        agg.set(le.id, le)
        return agg
      }, new Map<string, EntityLegalEntity>()),
    [includeSharedEntities, aclBasedLes, orgBasedLes]
  )
}
